/**
 * Public bridge from `/invite/:code` to the active app's invite URL.
 *
 * Custom apps (e.g. meal-planner / Pepper) own their own invite flow at
 * `<mount-prefix>/invite/:code`. The bare `/invite/CODE` URL is friendlier
 * to share in email/SMS — this component looks up the tenant's installed
 * apps and redirects.
 *
 * Policy: pick the first installed app mount. Tenants that install multiple
 * invite-aware apps can disambiguate by exposing different code prefixes
 * (e.g. PEP-xxx vs OTHER-xxx) and routing by that — call that out when it
 * actually happens.
 */
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAppMounts } from '@/lib/apps-mounts';
import { useAuthStore } from '@/stores/auth-store';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

export default function InviteRedirect() {
  const { code } = useParams<{ code: string }>();
  const location = useLocation();
  const { data: mounts, isLoading } = useAppMounts();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  // Wait for BOTH the mount list and the auth bootstrap before deciding —
  // otherwise a hard load of the invite link would flash an unauthenticated
  // redirect for a user who actually has a session.
  if (isLoading || !isInitialized) return <LoadingSpinner />;

  if (!code || !mounts || mounts.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">Invite link not available</h1>
        <p className="text-sm text-stone-600">
          This site doesn't have an app installed that handles invites.
        </p>
      </div>
    );
  }

  // The invitee must be signed in before we hand them to the app's accept
  // surface: the embedded app only receives a session JWT once the host has an
  // authenticated session (POST /apps/session reads the Rumio cookie). Bounce
  // anonymous visitors through login with this invite URL as the return
  // target — Login honors `?redirect=` (validated by isSafeReturnUrl), and for
  // social logins AuthCallback restores it from sessionStorage.
  if (!user) {
    const here = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(here)}`} replace />;
  }

  const target = `/${mounts[0].path_prefix}/invite/${encodeURIComponent(code)}`;
  return <Navigate to={target} replace />;
}
